import React, { useEffect, useState, useCallback } from 'react';
import { salesAPI } from '../../services/garageApi';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Eye,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  ShoppingCart,
  DollarSign,
  Clock,
  CheckCircle
} from 'lucide-react';

const Sales = () => {
  const navigate = useNavigate();

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    customer_id: '',
    status: '',
    date_from: '',
    date_to: ''
  });

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: currentPage,
        per_page: itemsPerPage
      };
      // Only send non-empty filters
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const res = await salesAPI.getAll(params);
      const list = res.data || [];
      setSales(Array.isArray(list) ? list : []);

      if (res.pagination) {
        setTotalPages(res.pagination.last_page || 1);
        setTotalItems(res.pagination.total || list.length);
      } else {
        setTotalPages(1);
        setTotalItems(list.length);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message);
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;
    try {
      await salesAPI.delete(id);
      fetchSales();
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || err.response?.data?.message || 'Error deleting sale.';
      alert(message);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSales();
  };

  const clearFilters = () => {
    setFilters({ customer_id: '', status: '', date_from: '', date_to: '' });
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      partial: { color: 'bg-blue-100 text-blue-800', icon: DollarSign },
      cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
    };
    return config[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock };
  };

  // Summary stats
  const totalAmount = sales.reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
  const paidCount = sales.filter(s => s.status === 'paid').length;
  const pendingCount = sales.filter(s => s.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 flex items-center space-x-3">
          <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
            <ShoppingCart className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Sales</p>
            <p className="text-lg font-semibold">{totalItems}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex items-center space-x-3">
          <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg">
            <DollarSign className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Page Total</p>
            <p className="text-lg font-semibold">${totalAmount.toFixed(2)}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex items-center space-x-3">
          <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Paid</p>
            <p className="text-lg font-semibold">{paidCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex items-center space-x-3">
          <div className="flex-shrink-0 p-2 bg-yellow-100 rounded-lg">
            <Clock className="h-5 w-5 text-yellow-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-lg font-semibold">{pendingCount}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h5 className="text-lg font-semibold">Sales</h5>
            <button
              className="inline-flex items-center space-x-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              onClick={() => navigate('/sales/create')}
            >
              <Plus className="h-4 w-4" />
              <span>New Sale</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b border-gray-200 p-4">
          <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            <input
              type="text"
              name="customer_id"
              placeholder="Customer ID..."
              value={filters.customer_id}
              onChange={handleFilterChange}
              className="form-input rounded-md border-gray-300"
            />
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="form-select rounded-md border-gray-300"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
              className="form-input rounded-md border-gray-300"
              placeholder="From"
            />
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
              className="form-input rounded-md border-gray-300"
              placeholder="To"
            />
            <div className="flex space-x-2">
              <button
                type="submit"
                className="inline-flex items-center space-x-1 rounded-md bg-gray-600 px-4 py-2 text-sm text-white hover:bg-gray-700"
              >
                <Search className="h-4 w-4" />
                <span>Filter</span>
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-6 py-3">Sale ID</th>
                    <th className="px-6 py-3">Customer</th>
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Total Amount</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sales.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        No sales found.
                      </td>
                    </tr>
                  ) : (
                    sales.map((sale) => {
                      const badge = getStatusBadge(sale.status);
                      const BadgeIcon = badge.icon;
                      return (
                        <tr key={sale.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className="text-sm font-medium text-gray-900">{sale.id}</span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            {sale.customer?.name || '-'}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            {sale.sale_date ? new Date(sale.sale_date).toLocaleDateString() : '-'}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                            ${Number(sale.total_amount || 0).toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className={`inline-flex items-center space-x-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.color}`}>
                              <BadgeIcon className="h-3 w-3" />
                              <span className="capitalize">{sale.status}</span>
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                className="rounded p-1 text-green-600 hover:bg-green-50 hover:text-green-900"
                                onClick={() => navigate(`/sales/${sale.id}`)}
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900"
                                onClick={() => handleDelete(sale.id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                      <span className="font-medium">{totalItems}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage >= totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Sales;
